// Position model for on-the-vessel use - only contains position for the vessel you are on

import { IndexedDBService } from '../../indexed-db/services/db_service';
import { PositionsService } from '../services/positions-service';
import { VesselPositionData, PositionData } from '../services/types/position-service.types';
import { VesselPositionModelBase } from './vessel-position-model';
import { isOnline } from '../utils/helpers';
import { IMO, VoyageImoModel } from './voyage-imo-model';
import { VoyageImoEventNames } from './voyage-imo-model-impl';
import { VoyageSchedulesModel } from './voyage-schedules-model';

const CacheTimeoutMinutes = 5;

const isVesselOnCloud: boolean = (window as any).appenv?.vesselOnCloud;

export type VesselPositionStoredEntry = {
    data: VesselPositionData
    fetchedTs: number
    id?: string
}

export class VesselPositionModelImpl extends VesselPositionModelBase
{
    constructor()
    {
        super();
        this._imo = VoyageImoModel!.imo;

        this._initPosition();
        let intervalID: number;
        intervalID = setInterval(() => this._fetchLatestPosition(), CacheTimeoutMinutes*60000);

        this._fetchTrack();
        this.addEventListener('position-updated', () => this._fetchTrack());
        VoyageSchedulesModel!.addEventListener('schedule-updated', () => this._fetchTrack());               
        VoyageImoModel!.addEventListener(VoyageImoEventNames.ImoUpdated, async () => {
            this._imo = VoyageImoModel!.imo;
            // clear the position info in indexed db when IMO is changed.
            if (VoyageImoModel!.imo){
                await IndexedDBService.position.delete();
            }
            await this._fetchLatestPosition(true);
            if (intervalID) {
                clearInterval(intervalID); // Stop the existing interval
            }
            intervalID = setInterval(async () => await this._fetchLatestPosition(), CacheTimeoutMinutes*60000); 
        })
    }

    get position()
    {
        return this._position ? this._position.data : null;
    }

    get track()
    {
        return this._track;
    }

    get usingManualPositionOverride()
    {
        return this._manualPositionOverride;
    }

    get malfunctioningGPS()
    {
        return this._malfunctioningGPS;
    }

    async setManualPositionOverride(override: boolean)
    {
        await PositionsService.setManualPositionOverride(override, this._imo);
        this._manualPositionOverride = override;
        this.dispatchEvent(new CustomEvent('position-updated'));
        await this._fetchLatestPosition(true);
    }

    async setManualPosition(coordinates: [number, number], timestamp?: string)
    {
        await PositionsService.setManualPosition(coordinates, timestamp ? timestamp : new Date().toISOString(), this._imo);
        await this._fetchLatestPosition(true);
    }

    private _position: VesselPositionStoredEntry | null = null;
    private _track: PositionData[] = [];
    private _manualPositionOverride: boolean = false;
    private _malfunctioningGPS: boolean = false;
    private _imo: IMO = null;

    private async _fetchLatestPosition(immediate?: boolean)
    {
        const currentTime = (new Date()).getTime();

        if (immediate || !this._position || (currentTime - this._position.fetchedTs) > CacheTimeoutMinutes*60000)
        {
            // When offline on cloud, use cached position from IndexedDB
            if (isVesselOnCloud && !isOnline()) {
                if (this._position) {
                    this.dispatchEvent(new CustomEvent('position-updated'));
                }
                return;
            }

            // Data stale (or not fetched at all yet) - update position
            const lastKnownPosition = await PositionsService.currentPosition_VesselVoyage([this._imo!]);
            if (lastKnownPosition && lastKnownPosition.length === 1)
            {
                this._position = { data: lastKnownPosition[0], fetchedTs: Date.now() };
                this._storePosition();
            }
            
            // Update manual override state
            try
            {
                this._manualPositionOverride = await PositionsService.getManualPositionOverride(this._imo!);
            }
            catch (e)
            {
                console.warn("Failed to get the manual position override state from positions API!");
            }
            
            // Check if papi data is not available or too out of date
            const outOfDateTime = 1000 * 60 * 30 // grace period of 30 minutes allowed before alerting user
            const dataSources = this._position?.data.dataSources || null
            const papiSource = dataSources?.['papi']
            const noPapi = !papiSource
            const papiOutdated = papiSource ? (new Date(papiSource.timestamp).getTime() + outOfDateTime < Date.now()) : false;
            const noValidSource = isVesselOnCloud
                ? !!this._position?.data.isManualNeeded
                : noPapi || papiOutdated

            this._malfunctioningGPS = !dataSources || noValidSource // on cloud it will refer to manualPositionNeeded
            this.dispatchEvent(new CustomEvent('position-updated'))

        }
    }

    private async _fetchTrack()
    {
        const dates = VoyageSchedulesModel!.getDatesFromSchedule(this._imo!);
        const trackData = await PositionsService.getVesselTrack_VesselVoyage(this._imo!, dates.fromDate, dates.toDate);
        
        if (!trackData)
        {
            console.warn("No track available!");
            return;
        }

        // TODO - move this logic to backend and make smarter (ie. take override timing and malfunctions into account)
        if (trackData.manualOverride)
        {
            // Ignore PAPI data when manual override is set
            this._track = trackData.positions.filter( position => position.source !== "papi" );
        }
        else
        {
            this._track = trackData.positions;
        }
        this.dispatchEvent(new Event('track-updated'));
    }

    private async _storePosition() {
        if (isVesselOnCloud) {
            if (!this._position) return;
            await IndexedDBService.position.add(this._position);
        } else {
            localStorage.setItem('position-updated', JSON.stringify(this._position));
        }
    }
    
    private async _initPosition() {
        await this._loadPosition();
        await this._fetchLatestPosition(true);
    }

    private async _loadPosition() {
        try {
            this._position = isVesselOnCloud
                ? await IndexedDBService.position.get() || null
                : JSON.parse(localStorage.getItem('position-updated') || 'null');
    
            this.setCacheLifecycle();

            // Dispatch with cached position so UI can render immediately
            if (this._position) {
                this.dispatchEvent(new CustomEvent('position-updated'));
            }
        } catch (error) {
            console.error("Error loading position:", error);
            await this._storePosition(); 
        }
    }
    
}
